import { createFileRoute, useSearch } from '@tanstack/react-router';
import { extractApiErrorDetail } from '@/lib/api-error';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import logo from '/logo_lightbg.svg';
import { apiEndpoint } from '@/lib/consts';

interface UnsubscribeSearchParams {
  token?: string;
}

export const Route = createFileRoute('/unsubscribe')({
  component: UnsubscribePage,
  validateSearch: (search: Record<string, unknown>): UnsubscribeSearchParams => {
    return {
      token: search.token as string | undefined,
    };
  },
});

interface UnsubscribeResponse {
  success: boolean;
  message: string;
}

async function unsubscribeWithToken(token: string): Promise<UnsubscribeResponse> {
  const response = await fetch(`${apiEndpoint}/unsubscribe?token=${encodeURIComponent(token)}`);
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(extractApiErrorDetail(errorData.detail) || 'Failed to unsubscribe');
  }
  
  return response.json();
}

function UnsubscribePage() {
  const { token } = useSearch({ from: '/unsubscribe' });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['unsubscribe', token],
    queryFn: () => unsubscribeWithToken(token!),
    enabled: !!token,
    retry: false,
  });

  // No token provided
  if (!token) {
    return (
      <UnsubscribeLayout>
        <Card className="w-full max-w-md rounded-lg border bg-white">
          <CardHeader className="p-6 pb-4 text-center">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-yellow-100 flex items-center justify-center">
                <XCircle className="h-8 w-8 text-yellow-600" />
              </div>
            </div>
            <CardTitle className="text-xl font-semibold text-gray-900">Invalid Link</CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0 text-center space-y-4">
            <p className="text-gray-700">
              This unsubscribe link is invalid. Please use the link from a recent email notification.
            </p>
            <Button variant="outline" asChild>
              <a href="/signin">
                Go to Sign In
                <ArrowRight className="h-4 w-4 ml-2" />
              </a>
            </Button>
          </CardContent>
        </Card>
      </UnsubscribeLayout>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <UnsubscribeLayout>
        <Card className="w-full max-w-md rounded-lg border bg-white">
          <CardContent className="p-6 py-12">
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="h-12 w-12 text-[#290BD9] animate-spin" />
              <p className="text-gray-700">Processing your request...</p>
            </div>
          </CardContent>
        </Card>
      </UnsubscribeLayout>
    );
  }

  // Error state
  if (isError) {
    return (
      <UnsubscribeLayout>
        <Card className="w-full max-w-md rounded-lg border bg-white">
          <CardHeader className="p-6 pb-4 text-center">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
            </div>
            <CardTitle className="text-xl font-semibold text-gray-900">Unable to Unsubscribe</CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0 text-center space-y-4">
            <p className="text-gray-700">
              {error instanceof Error ? error.message : 'Something went wrong. Please try again later.'}
            </p>
            <Button variant="outline" asChild>
              <a href="/signin">
                Sign in to manage preferences
                <ArrowRight className="h-4 w-4 ml-2" />
              </a>
            </Button>
          </CardContent>
        </Card>
      </UnsubscribeLayout>
    );
  }

  // Success state
  return (
    <UnsubscribeLayout>
      <Card className="w-full max-w-md rounded-lg border bg-white">
        <CardHeader className="p-6 pb-4 text-center">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <CardTitle className="text-xl font-semibold text-gray-900">Successfully Unsubscribed</CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0 text-center space-y-4">
          <p className="text-gray-700">{data?.message}</p>
          <p className="text-sm text-gray-500">
            You can re-enable email notifications anytime from your account settings.
          </p>
          <Button variant="outline" asChild>
            <a href="/signin">
              Go to Sign In
              <ArrowRight className="h-4 w-4 ml-2" />
            </a>
          </Button>
        </CardContent>
      </Card>
    </UnsubscribeLayout>
  );
}

function UnsubscribeLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="mb-8">
        <img src={logo} alt="Glitch" className="h-12" />
      </div>
      {children}
      <p className="mt-8 text-sm text-gray-500">
        © {new Date().getFullYear()} Glitch Ads. All rights reserved.
      </p>
    </div>
  );
}
